import { useEffect } from 'react';
import { isRunningAsPWA, getPlatform } from '../../utils/pwaDetection';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Records that the installed (home-screen) app was opened by the signed-in user,
 * at most once per user per UTC day. The CRM shows the result on the Family record
 * ("last opened the installed app on <date>"), so every gap here is a wrong answer:
 *  - waits for a signed-in user, and re-runs when the user changes (a second person
 *    signing in on the same phone is recorded too);
 *  - marks the day done only when the server says it RECORDED it — an anonymous
 *    call returns recorded:false, which used to burn the whole day;
 *  - re-checks when the app comes back to the foreground, so an app left open
 *    overnight still records the next day it is used.
 * Must sit inside AuthProvider.
 */
const PWASessionRecorder: React.FC = () => {
  const { user } = useAuth();
  const userId = user?.id;

  useEffect(() => {
    if (!userId || !isRunningAsPWA()) return;
    const key = `pwa_session_date:${userId}`;

    const record = (): void => {
      if (document.visibilityState === 'hidden') return;
      const today = new Date().toISOString().split('T')[0];
      try {
        if (localStorage.getItem(key) === today) return;
      } catch {
        // Storage blocked: record anyway; the server keeps one row per user per day.
      }
      api
        .post<{ recorded?: boolean }>('/pwa/session', { platform: getPlatform() })
        .then((res) => {
          if (!res.data?.recorded) return;
          try {
            localStorage.setItem(key, today);
          } catch {
            // ignore — see above
          }
        })
        .catch((err) => {
          // Don't block on errors, just log
          console.error('Failed to record PWA session:', err);
        });
    };

    record();
    document.addEventListener('visibilitychange', record);
    return () => document.removeEventListener('visibilitychange', record);
  }, [userId]);

  return null;
};

export default PWASessionRecorder;
